/**
 * Store Info › Team. Pure helpers for the people who can sign in to one
 * store: joining the two backend lists, role copy, validation and error text.
 *
 * `GET /api/stores/store-user/` carries names and emails; `GET
 * /api/stores/store-staff/` carries the membership id that role changes are
 * addressed by. Both are scoped to the current store by the backend.
 */

export type TeamRole = 'Owner' | 'Manager' | 'Staff';

export const TEAM_ROLES: TeamRole[] = ['Owner', 'Manager', 'Staff'];

export const ROLE_DESCRIPTION: Record<TeamRole, string> = {
  Owner: 'Everything, including the team and billing.',
  Manager: 'Runs the store: menu, integrations and notifications.',
  Staff: 'Day-to-day work: calls, orders and reservations.',
};

export interface ApiStoreUser {
  id: number;
  email?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  role?: string | null;
}

export interface ApiStoreStaff {
  id: number;
  user: number;
  role?: string | null;
  is_active?: boolean;
}

export interface TeamMember {
  userId: number;
  /** Membership id; role changes go through `store-staff/{staffId}/`. */
  staffId: number | null;
  firstName: string;
  lastName: string;
  email: string;
  role: TeamRole;
  isActive: boolean;
}

const ROLE_ORDER: Record<TeamRole, number> = { Owner: 0, Manager: 1, Staff: 2 };

export const toRole = (role: string | null | undefined): TeamRole =>
  TEAM_ROLES.includes(role as TeamRole) ? (role as TeamRole) : 'Staff';

export const memberName = (m: Pick<TeamMember, 'firstName' | 'lastName'>) =>
  `${m.firstName} ${m.lastName}`.trim();

/** Owners first, then Managers, then Staff; inactive last; then by name. */
export function joinTeam(
  users: ApiStoreUser[],
  staff: ApiStoreStaff[],
): TeamMember[] {
  const byUser = new Map(staff.map((s) => [s.user, s]));
  return users
    .map((u): TeamMember => {
      const membership = byUser.get(u.id);
      return {
        userId: u.id,
        staffId: membership?.id ?? null,
        firstName: (u.first_name ?? '').trim(),
        lastName: (u.last_name ?? '').trim(),
        email: (u.email ?? '').trim(),
        role: toRole(membership?.role ?? u.role),
        isActive: membership?.is_active ?? true,
      };
    })
    .sort(
      (a, b) =>
        Number(!a.isActive) - Number(!b.isActive) ||
        ROLE_ORDER[a.role] - ROLE_ORDER[b.role] ||
        (memberName(a) || a.email).localeCompare(memberName(b) || b.email),
    );
}

export function roleCounts(members: TeamMember[]): string {
  const parts = TEAM_ROLES.map((role) => {
    const n = members.filter((m) => m.role === role).length;
    if (!n) return null;
    const noun = role === 'Staff' ? 'staff' : role.toLowerCase();
    return `${n} ${noun}${n > 1 && role !== 'Staff' ? 's' : ''}`;
  }).filter(Boolean);
  return parts.join(' · ');
}

export const activeOwnerCount = (members: TeamMember[]) =>
  members.filter((m) => m.role === 'Owner' && m.isActive).length;

/**
 * Why this member's role can't be changed or they can't be removed by the
 * current user, or null when they can. Mirrors the backend rules so the
 * control explains itself instead of failing on save.
 */
export function lockedReason(
  member: TeamMember,
  members: TeamMember[],
  currentUserId: number | null | undefined,
  action: 'role' | 'remove',
): string | null {
  if (member.userId === currentUserId)
    return action === 'role'
      ? "You can't change your own role."
      : "You can't remove yourself.";
  if (
    member.role === 'Owner' &&
    member.isActive &&
    activeOwnerCount(members) <= 1
  )
    return action === 'role'
      ? 'The store needs at least one Owner. Make someone else an Owner first.'
      : "This is the store's only Owner. Make someone else an Owner first.";
  return null;
}

// ---------------------------------------------------------------------------
// Forms
// ---------------------------------------------------------------------------

export interface AddMemberForm {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  role: TeamRole;
}

export const EMPTY_ADD_FORM: AddMemberForm = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  role: 'Staff',
};

export type FormErrors<T> = Partial<Record<keyof T, string>>;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
export const MIN_PASSWORD = 8;

export function validateAddForm(
  form: AddMemberForm,
  existingEmails: string[],
): FormErrors<AddMemberForm> {
  const errors: FormErrors<AddMemberForm> = {};
  if (!form.firstName.trim()) errors.firstName = 'Add a first name.';
  if (!form.lastName.trim()) errors.lastName = 'Add a last name.';
  const email = form.email.trim().toLowerCase();
  if (!email) errors.email = 'Add an email address.';
  else if (!EMAIL_RE.test(email)) errors.email = 'Enter a valid email address.';
  else if (existingEmails.some((e) => e.toLowerCase() === email))
    errors.email = 'This person is already on the team.';
  if (form.password.length < MIN_PASSWORD)
    errors.password = `Use at least ${MIN_PASSWORD} characters.`;
  return errors;
}

export const addPayload = (form: AddMemberForm) => ({
  first_name: form.firstName.trim(),
  last_name: form.lastName.trim(),
  email: form.email.trim(),
  password: form.password,
  role: form.role,
});

export interface EditMemberForm {
  firstName: string;
  lastName: string;
  role: TeamRole;
}

export const editFormFrom = (m: TeamMember): EditMemberForm => ({
  firstName: m.firstName,
  lastName: m.lastName,
  role: m.role,
});

export function validateEditForm(
  form: EditMemberForm,
): FormErrors<EditMemberForm> {
  const errors: FormErrors<EditMemberForm> = {};
  if (!form.firstName.trim()) errors.firstName = 'Add a first name.';
  if (!form.lastName.trim()) errors.lastName = 'Add a last name.';
  return errors;
}

/** What an edit needs to send; null parts are unchanged. */
export function editChanges(member: TeamMember, form: EditMemberForm) {
  const firstName = form.firstName.trim();
  const lastName = form.lastName.trim();
  const nameChanged =
    firstName !== member.firstName || lastName !== member.lastName;
  return {
    name: nameChanged ? { first_name: firstName, last_name: lastName } : null,
    role: form.role !== member.role ? form.role : null,
  };
}

// ---------------------------------------------------------------------------
// Errors
// ---------------------------------------------------------------------------

const EXISTING_ACCOUNT =
  'Someone with this email already has an AskArthur account. Adding an existing account to this store isn’t supported here yet. Contact support and we’ll add them.';

/**
 * The staff endpoints answer with `{error}`, `{detail}`, `{message}` or DRF
 * field errors (`{email: [...], non_field_errors: [...]}`). Turn any of them
 * into one sentence.
 */
export function apiErrorMessage(data: unknown, fallback: string): string {
  if (!data || typeof data !== 'object') return fallback;
  const record = data as Record<string, unknown>;
  for (const key of ['error', 'detail', 'message']) {
    const value = record[key];
    if (typeof value === 'string' && value) return friendly(value);
  }
  for (const value of Object.values(record)) {
    const first = Array.isArray(value) ? value[0] : value;
    if (typeof first === 'string' && first) return friendly(first);
  }
  return fallback;
}

const friendly = (message: string) =>
  /already exists/i.test(message) && /email|user/i.test(message)
    ? EXISTING_ACCOUNT
    : message;
